import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LectureAiChip, SurfaceHero } from '../components/design-kit';
import { ArrowRight, BookText, ExternalLink, FileText, Globe, Loader2, Share2 } from 'lucide-react';

function htmlToExcerpt(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
}

export default function SharedPreviewPage() {
  const { kind, id } = useParams();
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      try {
        if (kind === 'note') {
          const note = await api.getLecture(id);
          if (isMounted) {
            setItem({
              kind: 'note',
              id: note.id,
              title: note.title || 'Untitled note',
              subjectId: note.subject_id,
              updatedAt: note.updated_at || note.created_at,
              bodyHtml: note.formatted_notes_html || '<p></p>',
              bodyText: note.formatted_notes || htmlToExcerpt(note.formatted_notes_html || ''),
              openPath: `/notes/${note.id}`,
            });
          }
          return;
        }

        if (kind === 'document') {
          const result = await api.getDocument(id);
          const document = result.document;
          if (isMounted) {
            setItem({
              kind: document.extra_metadata?.source_url ? 'link' : 'document',
              id: document.id,
              title: document.title,
              subjectId: document.subject_id,
              updatedAt: document.updated_at || document.created_at,
              sourceUrl: document.extra_metadata?.source_url || null,
              filename: document.original_filename,
              bodyText: document.extracted_text_preview || (document.has_extracted_text ? 'Text extracted and ready for memory-backed workflows.' : 'Stored in the library and ready to extract or review.'),
              openPath: '/library',
            });
          }
          return;
        }

        throw new Error('Unsupported shared preview');
      } catch (error) {
        console.error('Failed to load shared preview:', error);
        if (isMounted) {
          setItem(null);
          toast.error(error.message || 'Failed to load shared preview');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [id, kind]);

  const metaChips = useMemo(() => {
    if (!item) return [];
    if (item.kind === 'note') {
      return [{ label: 'Shared note', tone: 'note', icon: BookText }];
    }
    if (item.kind === 'link') {
      return [
        { label: 'Shared link', tone: 'link', icon: Globe },
        { label: item.filename, tone: 'document', icon: FileText },
      ];
    }
    return [{ label: 'Shared doc', tone: 'document', icon: FileText }];
  }, [item]);

  const shareLabel = item?.kind === 'note' ? 'Shared note preview' : item?.kind === 'link' ? 'Shared link preview' : 'Shared document preview';

  return (
    <div className="flex-1 overflow-y-auto bg-background">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <SurfaceHero
          eyebrow="Shared Preview"
          title={item?.title || 'Study artifact'}
          detail="A branded, read-friendly preview for notes, documents, and saved links shared inside Lecture AI."
          aside={item ? (
            <>
              <Badge variant="outline" className="rounded-xl px-3 py-2 text-xs">
                <Share2 className="mr-1 h-3.5 w-3.5" />
                {shareLabel}
              </Badge>
              <Button asChild className="rounded-xl">
                <Link to={item.openPath}>
                  Open in app
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </>
          ) : null}
        />

        <Card className="overflow-hidden border-border/60 bg-card/80 shadow-card">
          <CardContent className="space-y-6 p-6 sm:p-8">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading shared preview...
              </div>
            ) : !item ? (
              <div className="rounded-2xl border border-dashed border-border p-8 text-sm text-muted-foreground">
                This shared item could not be loaded.
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  {metaChips.map((chip) => (
                    <LectureAiChip
                      key={chip.label}
                      icon={chip.icon}
                      label={chip.label}
                      tone={chip.tone}
                    />
                  ))}
                  {item.updatedAt ? (
                    <LectureAiChip
                      label={`Updated ${formatDistanceToNow(new Date(item.updatedAt), { addSuffix: true })}`}
                      tone="neutral"
                    />
                  ) : null}
                </div>

                {item.kind === 'note' ? (
                  <div className="rounded-[28px] border border-violet-200/70 bg-violet-50/80 p-6">
                    <div
                      className="prose prose-slate max-w-none text-sm sm:text-base"
                      dangerouslySetInnerHTML={{ __html: item.bodyHtml }}
                    />
                  </div>
                ) : (
                  <div className="space-y-4 rounded-[28px] border border-emerald-200/70 bg-emerald-50/80 p-6">
                    {item.sourceUrl ? (
                      <LectureAiChip
                        icon={Globe}
                        label={item.sourceUrl}
                        tone="link"
                        className="max-w-full"
                      />
                    ) : null}
                    <p className="text-sm leading-7 text-foreground/80">{item.bodyText}</p>
                    {item.sourceUrl ? (
                      <Button asChild variant="outline" className="rounded-xl">
                        <a href={item.sourceUrl} target="_blank" rel="noreferrer">
                          Open original link
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
